/**
 * The SRP filter-combination indexability policy (TEC-697): which filter combinations are worth a
 * place in Google's index, based on what people actually search.
 *
 * This is the single source of truth for every surface that has to agree on it:
 *  - the page's `<meta name="robots">` and `X-Robots-Tag` header (`compute-srp-robots-content.ts`),
 *  - the SRP canonical URL (which filters it keeps: `SRP_CANONICAL_FILTER_ATTRIBUTES`),
 *  - `rel="nofollow"` on links pointing at SRPs (`srp-link-rel.ts`),
 *  - sitemaps and hub "linked" states (the `MIN_JOBS_FOR_INDEX` floor),
 *  - the `/backoffice/srp-indexability` explainer.
 *
 * Kept free of heavy imports (no SEO copy tables, no gazetteer) so the job-card links can import it
 * on the client.
 *
 * Rules, in order:
 *  1. fewer than MIN_JOBS_FOR_INDEX jobs → noindex (a quality floor, not a value signal),
 *  2. no filters → index,
 *  3. three or more filters → noindex,
 *  4. one filter → index when its type is searched on its own (`INDEXABLE_ALONE_GROUPS`),
 *  5. two filters → index only when the pair is one people search (`INDEXABLE_PAIRS`); a job
 *     type only pairs when it's one people use as a modifier (`PAIRABLE_JOB_TYPE_VALUES`),
 *  6. a place filter must be a real Finnish place (or a Remote-Finland/Europe hub).
 *
 * Evidence for the pair list (Ahrefs Finland volumes + GSC queries, 2026-09-28) is on Linear
 * TEC-697 under "Pair policy". Examples: "sairaanhoitaja työpaikat helsinki" 600/mo (role x place),
 * "keikkatyö helsinki" 700/mo (jobType x place), "lähihoitaja keikkatyö" 300/mo (role x jobType),
 * "sairaanhoitaja etätyö" 500/mo (role x remote). Role x language ("english software engineer
 * jobs finland") had no measurable demand and no traffic.
 *
 * A combination whose existing pages earn Google traffic stays indexable even without measurable
 * demand (`EARNS_TRAFFIC_PAIRS`): at our size any traffic is worth keeping. Before narrowing this
 * policy, check GSC for pages that would flip from index to noindex.
 */

import type { ActiveFilter, FilterType } from '../../types/srp-seo-meta'
import { toLocationSlug } from '../location/to-location-slug'
import { remoteHubScopeToLocationFacet, type RemoteHubEnglishScope } from '../location/remote-hub-scope'
import { getRegionTagType, getCountryCodeForRegionTagValue } from '../facets/region-tag-labels'
import { JOB_TYPE_TRANSLATIONS } from '../job/enum-translations'

/** Below this many live jobs an SRP is noindex. A quality floor only (a searched-for page with one
 *  or two listings reads as thin); search demand, not job count, decides which combinations index.
 *  Sitemaps and hub "linked" states use the same floor so they never point at a noindexed page. */
export const MIN_JOBS_FOR_INDEX = 3

/** What a filter means to a searcher. Two filters of the same group (a city and a region) never
 *  make an indexable pair. */
export type SrpFilterGroup =
  | 'role' | 'field' | 'place' | 'remote' | 'jobType' | 'language' | 'company' | 'industry' | 'roleLevel' | 'other'

const FILTER_GROUP: Record<FilterType, SrpFilterGroup> = {
  normalisedTitle: 'role',
  field: 'field',
  location: 'place',
  regionTags: 'place',
  remoteness: 'remote',
  jobType: 'jobType',
  requiredLanguages: 'language',
  company: 'company',
  normalisedRoleLevel: 'roleLevel',
  companyIndustry: 'industry',
  educationLevel: 'other',
  benefits: 'other',
  skillsAndTechnologies: 'other',
  peopleManagementTier: 'other',
  specialisations: 'other',
}

export function srpFilterGroup(type: FilterType): SrpFilterGroup {
  return FILTER_GROUP[type] ?? 'other'
}

/** Filter groups that are a search on their own ("sairaanhoitaja työpaikat", "työpaikat tampere",
 *  "osa-aikatyö", "english speaking jobs in finland", "kesko työpaikat"). Industry and role level
 *  are here because those pages already earn Google traffic (see EARNS_TRAFFIC below). Skills,
 *  benefits, education etc. alone had neither demand nor traffic. */
const INDEXABLE_ALONE_GROUPS: ReadonlySet<SrpFilterGroup> = new Set<SrpFilterGroup>([
  'role', 'field', 'place', 'remote', 'jobType', 'language', 'company', 'industry', 'roleLevel',
])

const pairKey = (a: SrpFilterGroup, b: SrpFilterGroup): string => [a, b].sort().join('+')

/**
 * Two-filter combinations that index. Anything not listed is noindex. Two sources:
 *  - search demand for the combination (the first block, evidence on TEC-697),
 *  - EARNS_TRAFFIC: combinations with no measurable demand whose existing pages still get Google
 *    impressions and clicks. Rule (2026-09-28): at our size any traffic is worth keeping, so a
 *    combination whose pages earn traffic never gets noindexed. GSC, 90 days to 2026-09-27:
 *    company + place 95 pages / 8 clicks / 742 impressions (mostly a city employer in its own
 *    city: Oulun kaupunki in Oulu, Turun kaupunki in Åbo), field + language 3 / 1 / 104,
 *    company + language 3 / 1 / 51, company + remote 1 / 0 / 3, company + field 1 / 0 / 2.
 */
// Searched but NOT indexed yet: role x jobType ("lähihoitaja keikkatyö" 300/mo), field x jobType
// ("hoitoalan keikkatyö" 150/mo), jobType x remote ("osa-aikainen etätyö" 150/mo). Those pages are
// a role/category/remote hub plus `?jobType=`, and the hubs render their own title and H1 with the
// job type left out ("Sairaanhoitajan työpaikat Suomessa" for part-time nurse jobs), so indexing
// them would publish duplicate titles. Add them here once the hub titles include the job type.
const EARNS_TRAFFIC_PAIRS = [
  pairKey('company', 'place'),
  pairKey('company', 'language'),
  pairKey('company', 'remote'),
  pairKey('company', 'field'),
  pairKey('field', 'language'),
]
const INDEXABLE_PAIRS: ReadonlySet<string> = new Set([
  ...EARNS_TRAFFIC_PAIRS,
  pairKey('role', 'place'),
  pairKey('role', 'remote'),
  pairKey('field', 'place'),
  pairKey('field', 'remote'),
  pairKey('jobType', 'place'),
  pairKey('place', 'remote'),
  pairKey('language', 'place'),
  pairKey('language', 'remote'),
])

/**
 * Job types people use as a modifier on another filter ("osa-aikatyö helsinki" 400/mo,
 * "keikkatyö helsinki" 700/mo, "kesätyö tampere" 1100/mo, "harjoittelupaikat helsinki" 60/mo).
 * Full-time, fixed-term, permanent, contract etc. are only searched on their own ("kokoaikainen
 * työ" 400/mo, "kokoaikainen työ helsinki" 0), and GSC showed `?field=retail&jobType=full-time`
 * ranking at positions 30-60 with zero clicks, so a jobType pair only indexes for these values.
 * Stored lowercase, every locale's facet value.
 */
const PAIRABLE_JOB_TYPE_VALUES: ReadonlySet<string> = new Set(
  (['Part-time', 'Freelance', 'Internship', 'Summer Job'] as const).flatMap((en) => [
    en,
    JOB_TYPE_TRANSLATIONS.en_to_fi[en],
    JOB_TYPE_TRANSLATIONS.en_to_sv[en],
  ]).map((v) => v.toLowerCase()),
)

function isPairableJobTypeValue(rawValue: string): boolean {
  return PAIRABLE_JOB_TYPE_VALUES.has(rawValue.trim().toLowerCase())
}

/** Every search attribute that can be part of an indexable SRP. The canonical URL keeps exactly
 *  these (and drops the rest), so an indexable page's canonical is itself rather than a less
 *  specific page. Derived from the groups above so it can't drift from the robots rules. */
export const SRP_CANONICAL_FILTER_ATTRIBUTES: readonly string[] = (() => {
  const attributeForType: Partial<Record<FilterType, string>> = { company: 'companyId' }
  return (Object.keys(FILTER_GROUP) as FilterType[])
    .filter((t) => INDEXABLE_ALONE_GROUPS.has(FILTER_GROUP[t]))
    .map((t) => attributeForType[t] ?? t)
})()

/** Slugs of the legitimate Remote-Finland / Remote-Europe location hubs, across all 3 locales. */
const ALLOWED_REMOTE_HUB_LOCATION_SLUGS: Set<string> = new Set(
  (['Finland', 'Europe'] as RemoteHubEnglishScope[]).flatMap(scope =>
    (['en', 'fi', 'sv'] as const).map(locale => toLocationSlug(remoteHubScopeToLocationFacet(locale, scope))),
  ),
)

/** Finland's country name as it appears as the trailing slug segment, per locale (en/sv share
 *  "Finland"). Lowercase because `toLocationSlug` canonicalizes to lowercase. */
const FINLAND_COUNTRY_SLUG_SUFFIXES = ['_finland', '_suomi']

/**
 * True for any real Finnish location (any city/town, not just a curated list; deliberately a
 * country-suffix check rather than a maintained city allowlist, since `isKnownFinlandLocationSlug`
 * only covers the cities featured on the `/locations` hub), or one of the Remote-Finland/
 * Remote-Europe hubs.
 */
function isIndexableLocationValue(rawValue: string): boolean {
  const slug = toLocationSlug(rawValue)
  if (ALLOWED_REMOTE_HUB_LOCATION_SLUGS.has(slug)) return true
  return FINLAND_COUNTRY_SLUG_SUFFIXES.some(suffix => slug.endsWith(suffix))
}

/**
 * regionTags mirror the `location` policy: Finnish cities and admin1 regions always index,
 * countries only when they are Finland itself, and continent/recruiting-region buckets
 * ("Europe", "APAC") never (they aren't a location intent a Finnish job board should rank for).
 */
function isIndexableRegionTagValue(rawValue: string): boolean {
  const type = getRegionTagType(rawValue)
  if (type === 'city' || type === 'admin1') return true
  if (type === 'country') return getCountryCodeForRegionTagValue(rawValue) === 'FI'
  return false
}

export interface RobotsDirectiveExplanation {
  directive: 'index, follow' | 'noindex, follow'
  /** Ordered trace of which rule decided the outcome: a side-output of the same branches that
   *  decide the directive, not a separate description that can drift from them. */
  reasons: string[]
}

const NOINDEX = 'noindex, follow' as const
const INDEX = 'index, follow' as const

/** The filter-combination policy, with the reasons trace the backoffice explainer shows. */
export function explainRobotsDirective(filters: ActiveFilter[], count: number | null): RobotsDirectiveExplanation {
  const reasons: string[] = []

  if (count !== null && count < MIN_JOBS_FOR_INDEX) {
    reasons.push(`Only ${count} matching job${count === 1 ? '' : 's'} (fewer than ${MIN_JOBS_FOR_INDEX} minimum) → noindex`)
    return { directive: NOINDEX, reasons }
  }

  if (filters.length === 0) {
    reasons.push('No filters applied → index')
    return { directive: INDEX, reasons }
  }

  if (filters.length >= 3) {
    reasons.push(`${filters.length} filters applied (3+ always noindex) → noindex`)
    return { directive: NOINDEX, reasons }
  }

  const groups = filters.map((f) => srpFilterGroup(f.type))
  if (filters.length === 1) {
    if (!INDEXABLE_ALONE_GROUPS.has(groups[0])) {
      reasons.push(`Single filter "${filters[0].type}" is not searched on its own → noindex`)
      return { directive: NOINDEX, reasons }
    }
    reasons.push(`Single filter "${filters[0].type}" is searched on its own → index`)
  } else {
    const shape = `${filters[0].type} + ${filters[1].type}`
    if (!INDEXABLE_PAIRS.has(pairKey(groups[0], groups[1]))) {
      reasons.push(`Pair ${shape} (${groups[0]} x ${groups[1]}) is not a combination people search → noindex`)
      return { directive: NOINDEX, reasons }
    }
    const jobTypeFilter = filters.find((f) => f.type === 'jobType')
    if (jobTypeFilter && !isPairableJobTypeValue(jobTypeFilter.rawValue)) {
      reasons.push(`Job type "${jobTypeFilter.value}" is only searched on its own, not combined with another filter → noindex`)
      return { directive: NOINDEX, reasons }
    }
    reasons.push(`Pair ${shape} (${groups[0]} x ${groups[1]}) is a combination people search → index`)
  }

  // A location that isn't a real Finnish place or a Remote-Finland/Remote-Europe hub (e.g. a
  // foreign country Google guessed its way into) never earns indexing.
  const locationFilter = filters.find(f => f.type === 'location')
  if (locationFilter && !isIndexableLocationValue(locationFilter.rawValue)) {
    reasons.push(`Location "${locationFilter.value}" is not a known Finnish location or Remote-Finland/Remote-Europe hub → noindex`)
    return { directive: NOINDEX, reasons }
  }
  if (locationFilter) reasons.push(`Location "${locationFilter.value}" is a known Finnish location or remote hub`)

  const regionTagFilter = filters.find(f => f.type === 'regionTags')
  if (regionTagFilter && !isIndexableRegionTagValue(regionTagFilter.rawValue)) {
    reasons.push(`Region tag "${regionTagFilter.value}" is not a Finnish city/region/country → noindex`)
    return { directive: NOINDEX, reasons }
  }
  if (regionTagFilter) reasons.push(`Region tag "${regionTagFilter.value}" is a Finnish location`)

  return { directive: INDEX, reasons }
}

export function getRobotsDirective(filters: ActiveFilter[], count: number | null): string {
  return explainRobotsDirective(filters, count).directive
}
